import React, { useState } from 'react';
import {
  Box, Typography, Tab, Tabs, Paper, Divider,
  Stack, Container, useTheme, alpha
} from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  BiDetail,
  BiEdit,
  BiBookOpen,
  BiFolderOpen,
  BiLayer
} from 'react-icons/bi';

import CourseSession from './course-session';
import Module from './Module';
import TrainingRoutine from './trainingModule';
import PreTest from './Assignment/PreTest';
import PostTest from './Assignment/PostTest';
import UserResourcesList from './UserResourcesList';
import { useGetEvaluationFormQuery } from 'redux/reducers/assignmentEvalutaion';
import UserGalleryList from './UserGalleryList';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

const CustomTabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const DashboardTrainee = () => {
  const theme = useTheme();
  const [activeModule, setActiveModule] = useState('');
  const [routineTabValue, setRoutineTabValue] = useState(0);
  const { training_id: routineId } = useParams<{ training_id: string }>();

  const handleRoutineTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setRoutineTabValue(newValue);
  };

  const { data: preTestQuestionsList } = useGetEvaluationFormQuery({
    evaluationPhase: 1,
    routineId: Number(routineId)
  });

  const { data: postTestQuestionsList } = useGetEvaluationFormQuery({
    evaluationPhase: 2,
    routineId: Number(routineId)
  });

  const handleReset = () => setRoutineTabValue(0);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper'
        }}
      >
        {/* --- DASHBOARD HEADER / TABS --- */}
        <Box sx={{ px: 3, pt: 2, bgcolor: '#ffffff' }}>
          <Tabs
            value={routineTabValue}
            onChange={handleRoutineTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                minWidth: 120,
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main' }
              }
            }}
          >
            <Tab icon={<BiDetail size={20} />} iconPosition="start" label="Routine Detail" />
            <Tab icon={<BiEdit size={20} />} iconPosition="start" label="Pre Test" />
            <Tab icon={<BiEdit size={20} />} iconPosition="start" label="Post Test" />
            <Tab icon={<BiFolderOpen size={20} />} iconPosition="start" label="Resources" />
            <Tab icon={<BiFolderOpen size={20} />} iconPosition="start" label="Gallery" />

          </Tabs>
        </Box>
        <Divider />

        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#fbfcfd' }}>
          {/* TAB 0: ROUTINE & MODULES */}
          <CustomTabPanel value={routineTabValue} index={0}>
            <Stack spacing={4}>
              {/* Routine Detail Card */}
              <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <TrainingRoutine />
              </Box>

              {/* Modules Workspace */}
              <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <BiLayer size={24} color={theme.palette.primary.main} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Training Modules
                  </Typography>
                </Stack>
                <Module
                  activeModule={activeModule}
                  onSetActiveModule={(moduleId: string) => setActiveModule(moduleId)}
                />
              </Box>

              {/* Sessions Display */}
              <Box sx={{ p: 3, bgcolor: 'white', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                  <BiBookOpen size={24} color={theme.palette.primary.main} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Session Activities
                  </Typography>
                </Stack>
                <CourseSession activeModule={activeModule}


                />
              </Box>
            </Stack>
          </CustomTabPanel>

          {/* TAB 1: PRE-TEST */}
          <CustomTabPanel value={routineTabValue} index={1}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2) }}>
              <PreTest
                handleRoutineTabChange={handleReset}
                pretestDetails={preTestQuestionsList?.data}
              />
            </Paper>
          </CustomTabPanel>

          {/* TAB 2: POST-TEST */}
          <CustomTabPanel value={routineTabValue} index={2}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2) }}>
              <PostTest
                handleRoutineTabChange={handleReset}
                posttestDetails={postTestQuestionsList?.data}
              />
            </Paper>
          </CustomTabPanel>

          {/* TAB 3: RESOURCES */}
          <CustomTabPanel value={routineTabValue} index={3}>
            <UserResourcesList />
          </CustomTabPanel>

          {/* TAB 4: GALLERY */}
          <CustomTabPanel value={routineTabValue} index={4}>
            <UserGalleryList  />
          </CustomTabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default DashboardTrainee;